import { OrganizationContentModel } from '@cibo/core'
import { BrandLogo } from '@cibo/profile'
import { FriendlyError, Markdown } from '@cibo/ui'
import {
  Box,
  Button,
  DialogActions,
  DialogTitle,
  LinkProps,
  Link as MUILink,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { UseQueryResult } from '@tanstack/react-query'
import { Entry } from 'contentful'
import { TFunction, useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth, useWindowTitle } from '../../hooks'
import { AUTH_ROUTES } from '../../pages'
import { useOrganizationContent } from '../../queries'

export const TermsDisplay = () => {
  const { isLoggedIn } = useAuth()
  const orgContent = useOrganizationContent()
  const { pathname } = useLocation()
  const { t } = useTranslation('@cibo/profile/TermsDisplay')
  const navigate = useNavigate()

  const ephemeralTermName = t(
    AUTH_ROUTES.TERMS_OF_USE === pathname ? 'termsOfUse' : 'privacyPolicy'
  )
  useWindowTitle(ephemeralTermName)

  const terms =
    pathname === AUTH_ROUTES.TERMS_OF_USE
      ? orgContent.data?.fields.termsPolicyDataUse
      : orgContent.data?.fields.privacyPolicy

  if (
    !orgContent.isLoading &&
    !orgContent.isFetching &&
    !orgContent.isError &&
    pathname === AUTH_ROUTES.PRIVACY_POLICY &&
    !orgContent.data?.fields.privacyPolicy
  ) {
    navigate(AUTH_ROUTES.TERMS_OF_USE, { replace: true })
  }

  return (
    <Stack spacing={2}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{!terms ? ephemeralTermName : terms?.fields.title}</Typography>
          <BrandLogo variant="square" size="large" />
        </Stack>
      </DialogTitle>
      {orgContent.isError ? (
        <Stack padding={4}>
          <FriendlyError
            message={t('termsError', {
              termName: ephemeralTermName,
            })}
          />
        </Stack>
      ) : orgContent.isLoading || orgContent.isFetching ? (
        <Stack padding={2}>
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Skeleton variant="text" />
        </Stack>
      ) : (
        <Box
          sx={{
            pl: 2,
            pr: 1,
            maxHeight: 'calc(100vh - 300px)',
            overflowX: 'hidden',
          }}
        >
          <Markdown
            overrides={{
              a: {
                component: ({ href, ...linkProps }: LinkProps) => <MUILink {...linkProps} />,
              },
            }}
          >
            {terms?.fields.agreementMarkdownLong ?? terms?.fields.agreementMarkdown}
          </Markdown>
        </Box>
      )}
      <TermsActions t={t} isLoggedIn={isLoggedIn} orgContent={orgContent} pathname={pathname} />
    </Stack>
  )
}

const TermsActions = ({
  isLoggedIn,
  orgContent,
  pathname,
  t,
}: {
  isLoggedIn: boolean
  orgContent: UseQueryResult<Entry<OrganizationContentModel>, unknown>
  pathname: string
  t: TFunction
}) => (
  <DialogActions>
    <Stack
      direction="row-reverse"
      spacing={2}
      justifyContent="space-between"
      sx={{ width: '100%' }}
    >
      {isLoggedIn ? (
        <>
          <Button variant="contained" component={Link} to={'/'}>
            {t('back')}
          </Button>
        </>
      ) : (
        <Button variant="contained" component={Link} to={AUTH_ROUTES.LOGIN}>
          {t('login')}
        </Button>
      )}
      {!!orgContent.data?.fields.privacyPolicy && ( // privacy policy is optional, only present toggle if both are present
        <Button
          color="secondary"
          variant="text"
          component={Link}
          to={
            pathname === AUTH_ROUTES.PRIVACY_POLICY
              ? AUTH_ROUTES.TERMS_OF_USE
              : AUTH_ROUTES.PRIVACY_POLICY
          }
        >
          {t(pathname === AUTH_ROUTES.PRIVACY_POLICY ? 'termsOfUse' : 'privacyPolicy')}
        </Button>
      )}
    </Stack>
  </DialogActions>
)
